import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import CameraCapture from '../components/CameraCapture';
import { IssueVideoList, AddIssueVideo, PendingVideoPicker, uploadPendingVideos, VideoProgress } from '../components/IssueVideos';
import { IssueDocs, PendingDocPicker, uploadPendingDocs } from '../components/IssueDocs';
import { Flag, Plus, RefreshCw, Search, Loader, MapPin, User, CalendarDays, Trash2, Mail, Send, X, Camera, ImageIcon, Video, FileText } from 'lucide-react';

// Issue Register — every on-road problem reported for/by a driver (refused dock
// access, abusive customers, broken intercoms, a concierge refusing parcels…).
// The office records incidents here (e.g. the ones we email AP about) and drivers
// file their own from the portal's Customer Service → Route Issues tab; both land
// in this one register. Backed by driver-issues-sb (settings KV 'driver_issues').

const URL = '/.netlify/functions/driver-issues-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

export const ISSUE_CATEGORIES = [
  ['access', 'No access / entry refused'],
  ['customer', 'Abusive or difficult customer'],
  ['intercom', 'Broken intercom / buzzer'],
  ['refusal', 'Concierge / building refusing parcels'],
  ['animal', 'Dog or animal hazard'],
  ['safety', 'Safety hazard'],
  ['other', 'Other'],
];
const catLabel = (k) => (ISSUE_CATEGORIES.find(([c]) => c === k) || [k, k])[1];

const STATUS_META = {
  open: { label: 'Open', color: '#ef4444' },
  monitoring: { label: 'Monitoring', color: '#d97706' },
  resolved: { label: 'Resolved', color: '#16a34a' },
};

const labelStyle = { display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };
const inputStyle = { width: '100%', boxSizing: 'border-box' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '10px 12px', fontSize: '.85rem', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'top' };

const StatusChip = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.open;
  return <span style={{ fontSize: '.68rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.05em', color: m.color, border: `1px solid ${m.color}`, borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{m.label}</span>;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const emptyForm = () => ({ issue_date: new Date().toLocaleDateString('en-CA'), driver_login: '', category: 'access', location: '', title: '', description: '', ap_notified: false, ap_reference: '' });

export default function DriverIssuesPage() {
  const { addNotification } = useApp();
  const [issues, setIssues] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('current'); // current = open + monitoring
  // ?q= deep link from the global search palette pre-fills the search box.
  const [searchParams] = useSearchParams();
  const qParam = searchParams.get('q') || '';
  const [q, setQ] = useState(qParam);
  useEffect(() => { if (qParam) setQ(qParam); }, [qParam]);
  const [form, setForm] = useState(null);     // new-issue form fields, or null
  const [formPhotos, setFormPhotos] = useState([]);  // photos on the issue being created
  const [formPhotoKey, setFormPhotoKey] = useState(0);
  // Videos picked on the new-issue form. They upload once the issue exists; any that
  // fail keep the form open against that same issue (createdId) so Save retries just them.
  const [formVideos, setFormVideos] = useState([]);
  const [formDocs, setFormDocs] = useState([]);       // PDFs, same deferred upload as videos
  const [createdId, setCreatedId] = useState(null);
  const [saving, setSaving] = useState(null);   // null | { text, frac }
  const [openId, setOpenId] = useState(null); // expanded row

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api('list');
    if (r.success) setIssues(r.issues || []);
    else addNotification?.(r.error || 'Failed to load', 'error');
    setLoading(false);
  }, [addNotification]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api('driversList').then((r) => { if (r && r.success) setDrivers(r.drivers || []); }).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    let rows = issues;
    if (statusFilter === 'current') rows = rows.filter((i) => i.status !== 'resolved');
    else if (statusFilter !== 'all') rows = rows.filter((i) => i.status === statusFilter);
    const n = q.trim().toLowerCase();
    if (n) rows = rows.filter((i) => `${i.driver_login} ${i.location} ${i.title} ${i.description} ${catLabel(i.category)}`.toLowerCase().includes(n));
    return rows;
  }, [issues, statusFilter, q]);

  const openCount = issues.filter((i) => i.status === 'open').length;

  const submitNew = async () => {
    if (!form.driver_login) { addNotification?.('Choose the driver', 'error'); return; }
    if (!form.title && !form.description) { addNotification?.('Add a title or description', 'error'); return; }
    if (saving) return;
    let id = createdId;
    if (!id) {
      setSaving({ text: 'Saving…', frac: 0 });
      const r = await api('create', { ...form, photos: formPhotos.map((p) => p.split(',')[1]).filter(Boolean) });
      if (!r.success) { setSaving(null); addNotification?.(r.error || 'Failed to save', 'error'); return; }
      id = r.issue.id;
      setCreatedId(id);
      setFormPhotos([]);
    }
    const failed = [];
    if (formDocs.length) {
      const { left, error } = await uploadPendingDocs(api, id, formDocs, (text, frac) => setSaving({ text, frac }));
      setFormDocs(left);
      if (left.length) failed.push(`${left.length} PDF${left.length > 1 ? 's' : ''} (${error})`);
    }
    if (formVideos.length) {
      const { left, error } = await uploadPendingVideos(api, id, formVideos, (text, frac) => setSaving({ text, frac }));
      setFormVideos(left);
      if (left.length) failed.push(`${left.length} video${left.length > 1 ? 's' : ''} (${error})`);
    }
    if (failed.length) {
      setSaving(null);
      load();
      addNotification?.(`Issue saved, but ${failed.join(' and ')} did not upload. Press Retry to try again.`, 'error');
      return;
    }
    setSaving(null);
    addNotification?.(createdId ? 'Attachments added' : 'Issue recorded', 'success');
    closeForm();
    load();
  };
  const closeForm = () => { setForm(null); setFormPhotos([]); setFormVideos([]); setFormDocs([]); setCreatedId(null); setSaving(null); };

  const patch = async (id, fields) => {
    const r = await api('update', { id, fields });
    if (r.success) setIssues((prev) => prev.map((i) => (i.id === id ? r.issue : i)));
    else addNotification?.(r.error || 'Failed to update', 'error');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this issue from the register? This can’t be undone.')) return;
    const r = await api('remove', { id });
    if (r.success) { setOpenId(null); setIssues((prev) => prev.filter((i) => i.id !== id)); }
    else addNotification?.(r.error || 'Failed to delete', 'error');
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Flag size={22} style={{ color: 'var(--hp-gold)' }} /> Issue Register</h2>
          <p>{filtered.length} of {issues.length} shown · problems reported on the road — access refusals, difficult customers, hazards
            {openCount > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}> · {openCount} open</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={() => setForm(emptyForm())}><Plus size={14} /> Report an issue</button>
        </div>
      </div>

      <div className="page-body">
        {form && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 800, marginBottom: 12 }}>New issue</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 150px' }}>
                <label style={labelStyle}>Date of issue</label>
                <input type="date" className="input" style={inputStyle} value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
              </div>
              <div style={{ flex: '1 1 220px' }}>
                <label style={labelStyle}>Driver</label>
                <select className="input" style={inputStyle} value={form.driver_login} onChange={(e) => setForm({ ...form, driver_login: e.target.value })}>
                  <option value="">— choose driver —</option>
                  {drivers.map((d) => <option key={d.login} value={d.login}>{d.name ? `${d.login} · ${d.name}` : d.login}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 220px' }}>
                <label style={labelStyle}>Category</label>
                <select className="input" style={inputStyle} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {ISSUE_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
              <div style={{ flex: '1 1 260px' }}>
                <label style={labelStyle}>Location / address</label>
                <input className="input" style={inputStyle} value={form.location} placeholder="e.g. 388 George Street, Sydney 2000" onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div style={{ flex: '1 1 260px' }}>
                <label style={labelStyle}>Title</label>
                <input className="input" style={inputStyle} value={form.title} placeholder="Short summary, e.g. Loading dock access refused" onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>What happened</label>
              <textarea className="input" style={{ ...inputStyle, minHeight: 90 }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div style={{ marginTop: 12, maxWidth: 460 }}>
              <label style={labelStyle}>Photos ({formPhotos.length})</label>
              {formPhotos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  {formPhotos.map((p, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img src={p} alt={`Photo ${idx + 1}`} style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)', display: 'block' }} />
                      <button onClick={() => setFormPhotos((ps) => ps.filter((_, j) => j !== idx))} aria-label="Remove photo"
                        style={{ position: 'absolute', top: -7, right: -7, width: 22, height: 22, borderRadius: 999, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {formPhotos.length < 12 && (
                <CameraCapture key={formPhotoKey} nativeFirst guide="document" maxEdge={1600} quality={0.8}
                  onCapture={(d) => { setFormPhotos((ps) => [...ps, d]); setFormPhotoKey((k) => k + 1); }} />
              )}
            </div>
            <div style={{ marginTop: 12, maxWidth: 460 }}>
              <label style={labelStyle}>Videos ({formVideos.length}) — short clips, up to 50MB each</label>
              <PendingVideoPicker files={formVideos} setFiles={setFormVideos} />
            </div>
            <div style={{ marginTop: 12, maxWidth: 460 }}>
              <label style={labelStyle}>PDF documents ({formDocs.length}) — e.g. an MRS report; office only, up to 20MB each</label>
              <PendingDocPicker files={formDocs} setFiles={setFormDocs} />
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', fontWeight: 700, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.ap_notified} onChange={(e) => setForm({ ...form, ap_notified: e.target.checked })} /> Australia Post notified
              </label>
              {form.ap_notified && (
                <div style={{ flex: '1 1 300px' }}>
                  <label style={labelStyle}>AP reference (who / when / case no.)</label>
                  <input className="input" style={inputStyle} value={form.ap_reference} placeholder="e.g. Emailed Brad Henwood (Alexandria PDC) 11 Aug" onChange={(e) => setForm({ ...form, ap_reference: e.target.value })} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              {saving && <div style={{ flex: 1 }}><VideoProgress text={saving.text} frac={saving.frac} /></div>}
              <button className="btn btn-secondary" disabled={!!saving} onClick={closeForm}>{createdId ? 'Close (skip the rest)' : 'Cancel'}</button>
              <button className="btn btn-primary" disabled={!!saving} onClick={submitNew}>{saving ? 'Saving…' : createdId ? 'Retry uploads' : 'Save to register'}</button>
            </div>
          </div>
        )}

        <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 240px' }}>
            <label style={labelStyle}>Search driver / address / description</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
              <input className="input" style={{ ...inputStyle, paddingLeft: 30 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. GARCIAVIV, George Street…" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select className="input" style={{ ...inputStyle, width: 160 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="current">Open + Monitoring</option>
              <option value="open">Open</option>
              <option value="monitoring">Monitoring</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}>
              {issues.length === 0 ? <>No issues recorded yet. Click <strong>Report an issue</strong>, or drivers can log their own from the portal (Customer Service → Route Issues).</> : 'None match your filters.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
                <thead>
                  <tr>
                    <th style={th}>Date</th>
                    <th style={th}>Driver</th>
                    <th style={th}>Category</th>
                    <th style={th}>Location</th>
                    <th style={th}>Issue</th>
                    <th style={th}>AP</th>
                    <th style={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((i) => (
                    <React.Fragment key={i.id}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => setOpenId(openId === i.id ? null : i.id)}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(i.issue_date)}{i.ongoing && i.status !== 'resolved' && <div style={{ fontSize: '.68rem', fontWeight: 700, color: '#ef4444' }}>Ongoing</div>}</td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{i.driver_login}</td>
                        <td style={td}>{catLabel(i.category)}</td>
                        <td style={td}>{i.location || '—'}</td>
                        <td style={td}>{i.title || (i.description || '').slice(0, 80)}{i.source === 'portal' && <span style={{ marginLeft: 6, fontSize: '.66rem', color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', borderRadius: 4, padding: '1px 5px' }}>from driver</span>}</td>
                        <td style={td}>
                          {(i.photos || []).length > 0 && (
                            <span title={`${i.photos.length} photo${i.photos.length > 1 ? 's' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--hp-gold)', marginRight: 8, fontSize: '.75rem' }}>
                              <ImageIcon size={13} />{i.photos.length}
                            </span>
                          )}
                          {(i.videos || []).length > 0 && (
                            <span title={`${i.videos.length} video${i.videos.length > 1 ? 's' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--hp-gold)', marginRight: 8, fontSize: '.75rem' }}>
                              <Video size={13} />{i.videos.length}
                            </span>
                          )}
                          {(i.documents || []).length > 0 && (
                            <span title={`${i.documents.length} PDF${i.documents.length > 1 ? 's' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--hp-gold)', marginRight: 8, fontSize: '.75rem' }}>
                              <FileText size={13} />{i.documents.length}
                            </span>
                          )}
                          {i.ap_notified ? <Mail size={14} style={{ color: 'var(--hp-gold)' }} title={i.ap_reference || 'AP notified'} /> : (!(i.photos || []).length && !(i.videos || []).length && !(i.documents || []).length && <span style={{ color: 'var(--hp-dim)' }}>—</span>)}
                        </td>
                        <td style={td}><StatusChip status={i.status} /></td>
                      </tr>
                      {openId === i.id && (
                        <tr>
                          <td colSpan={7} style={{ ...td, background: 'var(--hp-surface)' }}>
                            <IssueDetail issue={i} onPatch={(fields) => patch(i.id, fields)} onRemove={() => remove(i.id)} onNoted={(iss) => setIssues((prev) => prev.map((x) => (x.id === iss.id ? iss : x)))} notify={addNotification} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Add photos to an issue from the office — the one the driver described on the phone,
// or a second shot they sent in. Each capture uploads immediately and the control comes
// straight back, so several photos go on without reopening anything.
function AddIssuePhotos({ issue, onAdded, notify }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const full = (issue.photos || []).length >= 12;

  const send = async (dataUrl) => {
    const b64 = (dataUrl || '').split(',')[1];
    if (!b64) return;
    setBusy(true);
    const r = await api('addPhotos', { id: issue.id, photos: [b64] });
    setBusy(false);
    setKey((k) => k + 1);
    if (r.success) onAdded?.(r.issue);
    else notify?.(r.error || 'Could not save the photo', 'error');
  };

  if (full) return null;
  if (!open) {
    return (
      <button className="btn btn-secondary btn-sm" style={{ marginBottom: 12 }} onClick={() => setOpen(true)}>
        <Camera size={13} /> {(issue.photos || []).length ? 'Add another photo' : 'Add a photo'}
      </button>
    );
  }
  return (
    <div style={{ marginBottom: 12, maxWidth: 420 }}>
      <CameraCapture key={key} nativeFirst guide="document" maxEdge={1600} quality={0.8} onCapture={send} />
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 6 }} onClick={() => setOpen(false)}>
        {busy ? 'Uploading…' : 'Done adding photos'}
      </button>
    </div>
  );
}

// One timeline note. Office notes get an Edit link for fixing typos; the server
// keeps the earlier wording in note.edits (it may already have gone to AP), and
// the note shows "edited" with the original on hover. Driver notes stay as written.
function IssueNote({ issue, note, index, onSaved, notify }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);
  const [busy, setBusy] = useState(false);
  const isDriver = note.by === issue.driver_login;

  const save = async () => {
    if (!text.trim()) return;
    if (text.trim() === note.text) { setEditing(false); return; }
    setBusy(true);
    const r = await api('editNote', { id: issue.id, index, at: note.at, text: text.trim() });
    setBusy(false);
    if (r.success) { setEditing(false); onSaved(r.issue); }
    else notify?.(r.error || 'Failed to save note', 'error');
  };

  const original = (note.edits || [])[0];
  return (
    <div style={{ marginBottom: 8, fontSize: '.84rem' }}>
      <span style={{ color: 'var(--hp-dim)', fontSize: '.72rem' }}>
        {fmtWhen(note.at)} · {note.by}
        {note.edited_at && (
          <span title={original ? `Originally: ${original.text}` : ''} style={{ marginLeft: 6, fontStyle: 'italic', cursor: original ? 'help' : 'default' }}>
            · edited {fmtWhen(note.edited_at)}{note.edited_by ? ` by ${note.edited_by}` : ''}
          </span>
        )}
        {!isDriver && !editing && (
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6, padding: '0 4px', fontSize: '.72rem', color: 'var(--hp-gold)' }}
            onClick={() => { setText(note.text); setEditing(true); }}>Edit</button>
        )}
      </span>
      {editing ? (
        <div style={{ marginTop: 4 }}>
          <textarea className="input" style={{ width: '100%', minHeight: 80, boxSizing: 'border-box' }} value={text} autoFocus
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <button className="btn btn-primary btn-sm" disabled={busy || !text.trim()} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ whiteSpace: 'pre-wrap' }}>{note.text}</div>
      )}
    </div>
  );
}

function IssueDetail({ issue, onPatch, onRemove, onNoted, notify }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [resolution, setResolution] = useState(issue.resolution || '');
  const [emailing, setEmailing] = useState(false);

  const sendNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    const r = await api('addNote', { id: issue.id, text: note.trim() });
    setBusy(false);
    if (r.success) { setNote(''); onNoted(r.issue); }
    else notify?.(r.error || 'Failed to add note', 'error');
  };

  const setStatus = (status) => {
    // Resolving carries whatever's typed in the resolution box along with it.
    onPatch(status === 'resolved' ? { status, resolution } : { status });
  };

  const btn = (status, label) => (
    <button key={status} className="btn btn-secondary btn-sm" disabled={issue.status === status}
      style={issue.status === status ? { opacity: 0.5 } : {}} onClick={() => setStatus(status)}>{label}</button>
  );

  return (
    <div style={{ padding: '4px 2px' }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
        <span><User size={12} /> {issue.driver_login} · reported by {issue.reported_by} ({issue.source === 'portal' ? 'driver portal' : 'office'})</span>
        <span><CalendarDays size={12} /> {issue.ongoing && issue.status !== 'resolved' ? `Ongoing and unresolved · first reported ${fmtDate(issue.issue_date)}` : fmtDate(issue.issue_date)}</span>
        {issue.location && <span><MapPin size={12} /> {issue.location}</span>}
      </div>
      {issue.title && <div style={{ fontWeight: 800, marginBottom: 6 }}>{issue.title}</div>}
      {issue.description && <div style={{ fontSize: '.88rem', whiteSpace: 'pre-wrap', lineHeight: 1.55, marginBottom: 10 }}>{issue.description}</div>}

      {(issue.photos || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ ...labelStyle, marginBottom: 6 }}>Photos ({issue.photos.length})</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {issue.photos.map((ph, idx) => (
              <a key={idx} href={ph.url} target="_blank" rel="noreferrer" title={ph.uploaded_at ? `Added ${fmtWhen(ph.uploaded_at)}${ph.by ? ` by ${ph.by}` : ''}` : ''}>
                <img src={ph.url} alt={`Issue photo ${idx + 1}`}
                  style={{ width: 132, height: 132, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)', display: 'block' }} />
              </a>
            ))}
          </div>
        </div>
      )}
      <AddIssuePhotos issue={issue} onAdded={onNoted} notify={notify} />

      {(issue.videos || []).length > 0 && <div style={{ ...labelStyle, marginBottom: 6 }}>Videos ({issue.videos.length})</div>}
      <IssueVideoList videos={issue.videos} />
      <AddIssueVideo api={api} issue={issue} onAdded={onNoted} />

      <IssueDocs api={api} issue={issue} onChange={onNoted} notify={notify} />

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, fontSize: '.85rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!issue.ap_notified} onChange={(e) => onPatch({ ap_notified: e.target.checked })} /> AP notified
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, cursor: 'pointer' }}
          title="A recurring problem: the email shows the date of issue as “Ongoing and unresolved” (with the date first reported) until it’s resolved">
          <input type="checkbox" checked={!!issue.ongoing} onChange={(e) => onPatch({ ongoing: e.target.checked })} /> Ongoing
        </label>
        {issue.ap_notified && (
          <input className="input" style={{ flex: '1 1 320px' }} value={issue.ap_reference || ''} placeholder="AP reference — who / when / case no."
            onChange={(e) => onNoted({ ...issue, ap_reference: e.target.value })} onBlur={(e) => onPatch({ ap_reference: e.target.value })} />
        )}
        <button className="btn btn-secondary btn-sm" onClick={() => setEmailing(true)}><Mail size={14} /> Email this issue</button>
      </div>

      {(issue.emails || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {issue.emails.map((e, idx) => (
            <div key={idx} style={{ fontSize: '.78rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Send size={11} style={{ color: 'var(--hp-gold)' }} /> Emailed to {(e.to || []).join(', ')}{(e.cc || []).length ? `, cc ${e.cc.join(', ')}` : ''}{(e.bcc || []).length ? `, bcc ${e.bcc.join(', ')}` : ''} — {fmtWhen(e.at)} by {e.by}
            </div>
          ))}
        </div>
      )}

      {(issue.notes || []).length > 0 && (
        <div style={{ borderLeft: '2px solid var(--hp-border)', paddingLeft: 12, marginBottom: 12 }}>
          {issue.notes.map((n, idx) => (
            <IssueNote key={`${idx}-${n.at}`} issue={issue} note={n} index={idx} onSaved={onNoted} notify={notify} />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input className="input" style={{ flex: 1 }} value={note} placeholder="Add an update — visible to the driver on their portal" onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendNote()} />
        <button className="btn btn-secondary" disabled={busy || !note.trim()} onClick={sendNote}>Add note</button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {btn('open', 'Open')}
        {btn('monitoring', 'Monitoring')}
        <input className="input" style={{ flex: '1 1 240px' }} value={resolution} placeholder="How it was resolved (kept on the record)" onChange={(e) => setResolution(e.target.value)} />
        <button className="btn btn-primary btn-sm" disabled={issue.status === 'resolved'} onClick={() => setStatus('resolved')}>Mark resolved</button>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', color: '#ef4444' }} onClick={onRemove} title="Delete from register"><Trash2 size={14} /></button>
      </div>
      {issue.status === 'resolved' && issue.resolution && (
        <div style={{ marginTop: 8, fontSize: '.84rem', color: '#16a34a' }}>Resolved{issue.resolved_at ? ` ${fmtWhen(issue.resolved_at)}` : ''} — {issue.resolution}</div>
      )}

      {emailing && (
        <EmailIssueModal issue={issue} notify={notify}
          onClose={() => setEmailing(false)}
          onSent={(iss) => { setEmailing(false); onNoted(iss); }} />
      )}
    </div>
  );
}

// ── Email an issue to AP managers ─────────────────────────────────────────────
// Saved address book (AP managers etc.) + one-off addresses; delivery goes via
// the same Gmail SMTP path as the nightly carry-over report (emailIssue action).

const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '32px 12px', overflowY: 'auto' };
const modalCard = { width: 620, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EmailIssueModal({ issue, notify, onClose, onSent }) {
  const [book, setBook] = useState(null);          // saved recipients, null while loading
  const [picked, setPicked] = useState({});        // email -> 'to' | 'cc' | 'bcc' (absent = not sending)
  const [extra, setExtra] = useState('');          // one-off addresses, comma-separated
  const [extraCc, setExtraCc] = useState('');
  const [extraBcc, setExtraBcc] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [subject, setSubject] = useState(
    `PH Bouveng Pty Ltd — delivery issue: ${issue.title || catLabel(issue.category)}${issue.location ? ' — ' + issue.location : ''}`);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api('recipientsGet').then((r) => setBook(r.success ? r.recipients || [] : []))
      .catch(() => setBook([]));
  }, []);

  const saveBook = async (list) => {
    setBook(list);
    const r = await api('recipientsSave', { recipients: list });
    if (r.success) setBook(r.recipients);
    else notify?.(r.error || 'Failed to save the address book', 'error');
  };

  const addToBook = () => {
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { notify?.('Enter a valid email address', 'error'); return; }
    if ((book || []).some((r) => r.email === email)) { notify?.('Already in the list', 'error'); return; }
    saveBook([...(book || []), { name: newName.trim(), email }]);
    setPicked((p) => ({ ...p, [email]: 'to' }));
    setNewName(''); setNewEmail('');
  };

  const removeFromBook = (email) => {
    saveBook((book || []).filter((r) => r.email !== email));
    setPicked((p) => { const n = { ...p }; delete n[email]; return n; });
  };

  const split = (v) => v.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
  const pickedAs = (slot) => Object.keys(picked).filter((e) => picked[e] === slot);
  const to = [...pickedAs('to'), ...split(extra)];
  const cc = [...pickedAs('cc'), ...split(extraCc)];
  const bcc = [...pickedAs('bcc'), ...split(extraBcc)];
  const total = to.length + cc.length + bcc.length;
  const badExtras = [...split(extra), ...split(extraCc), ...split(extraBcc)].filter((e) => !EMAIL_RE.test(e));

  const send = async () => {
    if (!to.length) { notify?.('Put at least one recipient in To', 'error'); return; }
    if (badExtras.length) { notify?.(`Not a valid email address: ${badExtras.join(', ')}`, 'error'); return; }
    setSending(true);
    const r = await api('emailIssue', { id: issue.id, to, cc, bcc, subject: subject.trim(), message: message.trim() });
    setSending(false);
    if (r.success) {
      const media = [r.photos && `${r.photos} photo${r.photos > 1 ? 's' : ''}`, r.videos && `${r.videos} video link${r.videos > 1 ? 's' : ''}`, r.documents && `${r.documents} PDF${r.documents > 1 ? 's' : ''}`].filter(Boolean);
      const pics = media.length ? ` with ${media.join(' and ')}` : '';
      const misses = [r.photosSkipped && `${r.photosSkipped} photo${r.photosSkipped > 1 ? 's' : ''}`, r.videosSkipped && `${r.videosSkipped} video${r.videosSkipped > 1 ? 's' : ''}`, r.docsSkipped && `${r.docsSkipped} PDF${r.docsSkipped > 1 ? 's' : ''}`].filter(Boolean);
      const miss = misses.length ? ` — ${misses.join(' and ')} could not be included` : '';
      const copies = (r.cc || []).length ? ` (cc ${r.cc.join(', ')})` : '';
      notify?.(`Issue emailed to ${r.to.join(', ')}${copies}${pics}${miss}`, miss ? 'error' : 'success');
      onSent(r.issue);
    }
    else notify?.(r.error || 'Failed to send', 'error');
  };

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && !sending && onClose()}>
      <div style={modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}><Mail size={17} style={{ color: 'var(--hp-gold)' }} /> Email this issue</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={sending}><X size={16} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <label style={labelStyle}>Recipients</label>
          {book === null ? (
            <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem' }}><Loader size={13} /> Loading saved recipients…</div>
          ) : book.length === 0 ? (
            <div style={{ color: 'var(--hp-dim)', fontSize: '.85rem', marginBottom: 6 }}>No saved recipients yet — add your AP managers below and they'll be here next time.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
              {book.map((r) => (
                <div key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.87rem', cursor: 'pointer', flex: 1, minWidth: 0 }}>
                    <input type="checkbox" checked={!!picked[r.email]}
                      onChange={(e) => setPicked((p) => { const n = { ...p }; if (e.target.checked) n[r.email] = 'to'; else delete n[r.email]; return n; })} />
                    <span style={{ fontWeight: 700 }}>{r.name || r.email}</span>
                    {r.name && <span style={{ color: 'var(--hp-dim)', fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email}</span>}
                  </label>
                  {picked[r.email] && (
                    <select className="input" aria-label={`Send ${r.name || r.email} as`} value={picked[r.email]}
                      style={{ width: 'auto', padding: '3px 6px', fontSize: '.8rem' }}
                      onChange={(e) => setPicked((p) => ({ ...p, [r.email]: e.target.value }))}>
                      <option value="to">To</option>
                      <option value="cc">Cc</option>
                      <option value="bcc">Bcc</option>
                    </select>
                  )}
                  <button className="btn btn-ghost btn-sm" title="Remove from saved recipients" style={{ color: 'var(--hp-dim)' }} onClick={() => removeFromBook(r.email)}><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <input className="input" style={{ flex: '1 1 160px' }} value={newName} placeholder="Name (e.g. Brad Henwood)" onChange={(e) => setNewName(e.target.value)} />
            <input className="input" style={{ flex: '1 1 200px' }} type="email" value={newEmail} placeholder="email@auspost.com.au" onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addToBook()} />
            <button className="btn btn-secondary btn-sm" onClick={addToBook} disabled={!newEmail.trim()}><Plus size={13} /> Save recipient</button>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>One-off addresses (not saved) — comma separated</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 8px', alignItems: 'center' }}>
              <span style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--hp-dim)' }}>To</span>
              <input className="input" style={inputStyle} value={extra} placeholder="someone@auspost.com.au, another@example.com" onChange={(e) => setExtra(e.target.value)} />
              <span style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--hp-dim)' }}>Cc</span>
              <input className="input" style={inputStyle} value={extraCc} placeholder="copied — everyone sees them" onChange={(e) => setExtraCc(e.target.value)} />
              <span style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--hp-dim)' }}>Bcc</span>
              <input className="input" style={inputStyle} value={extraBcc} placeholder="blind copy — hidden from the others" onChange={(e) => setExtraBcc(e.target.value)} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Subject</label>
            <input className="input" style={inputStyle} value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Message (optional — appears above the issue details)</label>
            <textarea className="input" style={{ ...inputStyle, minHeight: 70 }} value={message} placeholder="e.g. Hi Brad — flagging the below for your attention. Could you raise it with the building manager?" onChange={(e) => setMessage(e.target.value)} />
          </div>

          <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 14, lineHeight: 1.5 }}>
            The email includes the issue's date, driver, category, location, description and any update notes,
            in the same branded format as the nightly carry-over report. Sending marks the issue as "AP notified".
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
            <button className="btn btn-primary" onClick={send} disabled={sending || !to.length}>
              {sending ? <><Loader size={14} /> Sending…</> : <><Send size={14} /> Send to {total || '…'} recipient{total === 1 ? '' : 's'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
